import type { NewsItem, NewsResponse } from '../../types/news'

function editorialItems(edition: NewsResponse): NewsItem[] {
  return edition.items.filter((item) => item.kind === 'ai-synthesis' || item.sourceId.startsWith('omikina'))
}

function editorialRevisionTime(edition: NewsResponse): number {
  return editorialItems(edition).reduce((latest, item) => {
    const time = Date.parse(item.revisedAt ?? item.publishedAt)
    return Number.isFinite(time) ? Math.max(latest, time) : latest
  }, 0)
}

function editorialEditionKey(edition: NewsResponse): string {
  return JSON.stringify(editorialItems(edition)
    .map((item) => [
      item.id, item.publishedAt, item.revisedAt, item.homepageLeadUntil,
      item.title, item.summary, item.imageUrl, item.homepagePromotionEligible,
      item.publicationStatus, item.editorialReview, item.humanReview?.reviewedAt,
      item.featured, item.priority,
    ])
    .sort((left, right) => String(left[0]).localeCompare(String(right[0]))))
}

export function isNewerNewsEdition(current: NewsResponse | null, incoming: NewsResponse): boolean {
  if (!current) return true
  const currentTime = Date.parse(current.refreshedAt)
  const incomingTime = Date.parse(incoming.refreshedAt)
  if (!Number.isFinite(incomingTime)) return false
  if (!Number.isFinite(currentTime) || incomingTime > currentTime) return true
  if (incomingTime < currentTime) return false

  // Editorial publication and promotion can change between RSS cache refreshes.
  if (editorialRevisionTime(incoming) < editorialRevisionTime(current)) return false
  return editorialEditionKey(incoming) !== editorialEditionKey(current)
}

export function newestNewsData(current: NewsResponse | null, incoming: NewsResponse): NewsResponse {
  return isNewerNewsEdition(current, incoming) ? incoming : current ?? incoming
}
